import { Injectable } from '@angular/core';
import { ObjectState, StatefullValue } from '@meddev/fe-shared';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { DeviceInfo } from '../../interface';
import { MediaStateModel } from './media-state.model';
import { MediaActions } from './media.actions';

const MEDIA_STATE_TOKEN: StateToken<MediaStateModel> = new StateToken('MediaState');

const DEFAULT_STATE: MediaStateModel = {
    localMediaInitialized: false,
    isMicOn: true,
    isCameraOn: true,
    isAudioOn: true,
    selectedMicDevice: null,
    selectedCameraDevice: null,
    selectedAudioDevice: null,
    micDevices: { state: ObjectState.UNINIT, value: [] },
    cameraDevices: { state: ObjectState.UNINIT, value: [] },
    audioDevices: { state: ObjectState.UNINIT, value: [] },
    audioVolume: 100,
};

@State<MediaStateModel>({
    name: MEDIA_STATE_TOKEN,
    defaults: DEFAULT_STATE,
    children: [],
})
@Injectable()
export class MediaState {
    @Selector()
    static micDevices(state: MediaStateModel): StatefullValue<DeviceInfo[]> {
        return state.micDevices;
    }

    @Selector()
    static cameraDevices(state: MediaStateModel): StatefullValue<DeviceInfo[]> {
        return state.cameraDevices;
    }

    @Selector()
    static audioDevices(state: MediaStateModel): StatefullValue<DeviceInfo[]> {
        return state.audioDevices;
    }

    @Selector() static isMicOn(state: MediaStateModel) {
        return state.isMicOn;
    }
    @Selector() static isCameraOn(state: MediaStateModel) {
        return state.isCameraOn;
    }
    @Selector() static isAudioOn(state: MediaStateModel) {
        return state.isAudioOn;
    }

    @Selector() static audioVolume(state: MediaStateModel) {
        return state.audioVolume;
    }

    @Selector()
    static selectedMicDevice(state: MediaStateModel): DeviceInfo | null {
        return state.selectedMicDevice;
    }

    @Selector()
    static selectedCameraDevice(state: MediaStateModel): DeviceInfo | null {
        return state.selectedCameraDevice;
    }

    @Selector()
    static selectedAudioDevice(state: MediaStateModel): DeviceInfo | null {
        return state.selectedAudioDevice;
    }

    @Selector()
    static mediaInitialized(state: MediaStateModel) {
        return state.localMediaInitialized;
    }
    @Action(MediaActions.InitLocalMedia)
    initLocalMedia({ patchState }: StateContext<MediaStateModel>) {
        patchState({ localMediaInitialized: true });
    }

    @Action(MediaActions.DestroyLocalMedia)
    destroyLocalMedia({ patchState }: StateContext<MediaStateModel>) {
        patchState({ localMediaInitialized: false });
    }

    @Action(MediaActions.ToggleMic)
    toggleMic({ patchState, getState }: StateContext<MediaStateModel>) {
        const state = getState();
        const isOn = !state.isMicOn;
        let selected = state.selectedMicDevice;

        if (isOn && !selected && state.micDevices.value.length) {
            selected = state.micDevices.value[0];
            patchState({ selectedMicDevice: selected });
        }

        patchState({ isMicOn: isOn && !!selected });
    }

    @Action(MediaActions.ToggleCamera)
    toggleCamera({ patchState, getState }: StateContext<MediaStateModel>) {
        const state = getState();
        const isOn = !state.isCameraOn;
        let selected = state.selectedCameraDevice;

        if (isOn && !selected && state.cameraDevices.value.length) {
            selected = state.cameraDevices.value[0];
            patchState({ selectedCameraDevice: selected });
        }

        patchState({ isCameraOn: isOn && !!selected });
    }

    @Action(MediaActions.ToggleAudio)
    toggleAudio({ patchState, getState }: StateContext<MediaStateModel>, { volume }: MediaActions.ToggleAudio) {
        const state = getState();
        const newVolume = volume !== undefined ? volume : state.audioVolume === 0 ? 100 : 0;

        let selected = state.selectedAudioDevice;
        if (newVolume > 0 && !selected && state.audioDevices.value.length) {
            selected = state.audioDevices.value[0];
            patchState({ selectedAudioDevice: selected });
        }

        patchState({
            isAudioOn: newVolume > 0 && !!selected,
            audioVolume: newVolume,
        });
    }

    @Action(MediaActions.SetSelectedMicDevice)
    setMicDevice({ patchState }: StateContext<MediaStateModel>, { device }: MediaActions.SetSelectedMicDevice) {
        patchState({ selectedMicDevice: device });
    }

    @Action(MediaActions.SetSelectedCameraDevice)
    setCameraDevice({ patchState }: StateContext<MediaStateModel>, { device }: MediaActions.SetSelectedCameraDevice) {
        patchState({ selectedCameraDevice: device });
    }

    @Action(MediaActions.SetSelectedAudioDevice)
    setAudioDevice({ patchState }: StateContext<MediaStateModel>, { device }: MediaActions.SetSelectedAudioDevice) {
        patchState({ selectedAudioDevice: device });
    }

    @Action(MediaActions.SetAudioDevices)
    setAudioDevices(ctx: StateContext<MediaStateModel>, { devices }: MediaActions.SetAudioDevices) {
        MediaState.updateDeviceState(ctx, 'audioDevices', 'selectedAudioDevice', devices);
    }

    @Action(MediaActions.SetMicDevices)
    setMicDevices(ctx: StateContext<MediaStateModel>, { devices }: MediaActions.SetMicDevices) {
        MediaState.updateDeviceState(ctx, 'micDevices', 'selectedMicDevice', devices);
    }

    @Action(MediaActions.SetCameraDevices)
    setCameraDevices(ctx: StateContext<MediaStateModel>, { devices }: MediaActions.SetCameraDevices) {
        MediaState.updateDeviceState(ctx, 'cameraDevices', 'selectedCameraDevice', devices);
    }

    private static updateDeviceState<T extends DeviceInfo>(
        ctx: StateContext<MediaStateModel>,
        deviceListStateKey: keyof MediaStateModel,
        selectedDeviceStateKey: keyof MediaStateModel,
        availableDevices: T[],
    ): void {
        const state = ctx.getState();
        const previouslySelected = state[selectedDeviceStateKey] as T | null;

        if (availableDevices.length === 0) {
            ctx.patchState({
                [deviceListStateKey]: [],
                [selectedDeviceStateKey]: null,
            } as Partial<MediaStateModel>);
            return;
        }

        const isValid = previouslySelected ? availableDevices.some(d => d.deviceId === previouslySelected.deviceId) : false;
        const updatedSelectedDevice = isValid ? previouslySelected : availableDevices[0];

        ctx.patchState({
            [deviceListStateKey]: { state: ObjectState.VALID, value: availableDevices },
            [selectedDeviceStateKey]: updatedSelectedDevice,
        } as Partial<MediaStateModel>);
    }
}
